import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  TrendingUp, 
  PiggyBank, 
  Target, 
  Award,
  Star,
  Crown,
  Trophy,
  DollarSign,
  CreditCard,
  Shield,
  Users,
  BookOpen,
  TrendingDown
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

/**
 * Finance-Focused Centse Score Card Component
 * 
 * Enhanced version of CentseScoreCard specifically designed for the finance dashboard.
 * Emphasizes financial health metrics and transaction-based insights.
 * 
 * Finance Score Breakdown:
 * - Financial Health (30% weight) - Emergency fund, debt management, savings rate
 * - Transaction Wisdom (25% weight) - Smart spending patterns and budget compliance  
 * - Learning Progress (20% weight) - Finance-focused lessons completed
 * - Investment Readiness (15% weight) - Portfolio management and risk assessment
 * - Social Finance Score (10% weight) - Friend network financial health
 */
const CentseScoreFinance = () => {
  const [animatedScore, setAnimatedScore] = useState(0);

  // Fetch comprehensive financial data for score calculation
  const { data: financeScoreData } = useQuery({
    queryKey: ['centseFinanceScore'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      console.log("💰 Calculating Finance-Focused Centse Score for user:", user.id);

      // Get user progress for finance-related lessons
      const { data: userProgress } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id);

      // Get user points
      const { data: userPoints } = await supabase
        .from('user_points')
        .select('*')
        .eq('user_id', user.id)
        .single();

      // Get budgets for financial health assessment
      const { data: budgets } = await supabase
        .from('budgets')
        .select('*')
        .eq('user_id', user.id);

      // Get friends financial data
      const { data: friendsData } = await supabase
        .from('leaderboard_view')
        .select('*')
        .eq('is_friend', true);

      // Mock financial health data (replace with real financial APIs)
      const mockFinancialData = {
        emergencyFundRatio: 0.6, // 60% of 6-month goal
        debtToIncomeRatio: 0.08, // 8% - excellent
        savingsRate: 0.15, // 15% of income
        budgetCompliance: 0.85, // 85% budget adherence
        investmentAllocation: 0.10, // 10% of income to investments
        creditUtilization: 0.15, // 15% - good
        monthlyTransactions: 45,
        smartSpendingChoices: 38 // out of 45 transactions
      };

      // Calculate Financial Health Score (30% weight)
      const emergencyFundScore = Math.min(mockFinancialData.emergencyFundRatio * 100, 100);
      const debtManagementScore = Math.max(100 - (mockFinancialData.debtToIncomeRatio * 500), 0); // Lower debt = higher score
      const savingsScore = Math.min(mockFinancialData.savingsRate * 400, 100); // 25% savings rate = 100 points
      const creditScore = Math.max(100 - (mockFinancialData.creditUtilization * 200), 0); // Lower utilization = higher score
      
      const financialHealthScore = (emergencyFundScore + debtManagementScore + savingsScore + creditScore) / 4;

      // Calculate Transaction Wisdom Score (25% weight)  
      const budgetComplianceScore = mockFinancialData.budgetCompliance * 100;
      const smartSpendingScore = (mockFinancialData.smartSpendingChoices / mockFinancialData.monthlyTransactions) * 100;
      
      const transactionWisdomScore = (budgetComplianceScore + smartSpendingScore) / 2;

      // Calculate Learning Progress Score (20% weight)
      const completedLessons = userProgress?.filter(p => p.status === 'completed').length || 0;
      const financeCompletedLessons = Math.min(completedLessons, 25); // Cap at 25 finance lessons
      const learningProgressScore = (financeCompletedLessons / 25) * 100;

      // Calculate Investment Readiness Score (15% weight)
      const investmentScore = Math.min(mockFinancialData.investmentAllocation * 500, 100); // 20% allocation = 100 points
      const portfolioWisdomScore = Math.min(completedLessons * 4, 100); // Investment knowledge
      
      const investmentReadinessScore = (investmentScore + portfolioWisdomScore) / 2;

      // Calculate Social Finance Score (10% weight)
      let socialFinanceScore = 0;
      if (friendsData && friendsData.length > 0) {
        const friendsAveragePoints = friendsData.reduce((sum, friend) => sum + (friend.points || 0), 0) / friendsData.length;
        const socialScore = Math.min((friendsAveragePoints / 500) * 100, 100);
        const networkBonus = Math.min(friendsData.length * 10, 50); // Network effect bonus
        socialFinanceScore = (socialScore * 0.7) + (networkBonus * 0.3);
      }

      // Calculate Final Finance Centse Score (0-1000 scale)
      const financeScore = Math.round(
        (financialHealthScore * 0.30) +     // 30% Financial Health
        (transactionWisdomScore * 0.25) +   // 25% Transaction Wisdom
        (learningProgressScore * 0.20) +    // 20% Learning Progress
        (investmentReadinessScore * 0.15) + // 15% Investment Readiness
        (socialFinanceScore * 0.10)        // 10% Social Finance
      ) * 10; // Scale to 1000

      console.log("💰 Finance Score Breakdown:", {
        financialHealthScore,
        transactionWisdomScore,
        learningProgressScore,
        investmentReadinessScore,
        socialFinanceScore,
        finalScore: financeScore
      });

      return {
        score: Math.min(financeScore, 1000),
        breakdown: {
          financialHealth: Math.round(financialHealthScore),
          transactionWisdom: Math.round(transactionWisdomScore),
          learningProgress: Math.round(learningProgressScore),
          investmentReadiness: Math.round(investmentReadinessScore),
          socialFinance: Math.round(socialFinanceScore)
        },
        metrics: {
          completedLessons,
          emergencyFundProgress: Math.round(mockFinancialData.emergencyFundRatio * 100),
          budgetCompliance: Math.round(mockFinancialData.budgetCompliance * 100),
          savingsRate: Math.round(mockFinancialData.savingsRate * 100),
          friendsCount: friendsData?.length || 0
        },
        insights: {
          strongestArea: "Financial Health",
          improvementArea: "Investment Readiness",
          nextMilestone: financeScore >= 800 ? "Financial Guru" : financeScore >= 600 ? "Money Master" : "Finance Pro"
        }
      };
    }
  });

  // Animate score on load
  useEffect(() => {
    if (financeScoreData?.score) {
      const timer = setTimeout(() => {
        let current = 0;
        const increment = financeScoreData.score / 40; // Smoother animation
        const animate = () => {
          current += increment;
          if (current < financeScoreData.score) {
            setAnimatedScore(Math.round(current));
            requestAnimationFrame(animate);
          } else {
            setAnimatedScore(financeScoreData.score);
          }
        };
        animate();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [financeScoreData?.score]);

  // Determine financial level and badge
  const getFinanceLevel = (score: number) => {
    if (score >= 850) return { level: "Financial Guru", icon: Crown, color: "from-yellow-400 to-orange-500", bgColor: "bg-gradient-to-r from-yellow-100 to-orange-100" };
    if (score >= 700) return { level: "Money Master", icon: Trophy, color: "from-purple-400 to-pink-500", bgColor: "bg-gradient-to-r from-purple-100 to-pink-100" };
    if (score >= 550) return { level: "Finance Pro", icon: Award, color: "from-blue-400 to-cyan-500", bgColor: "bg-gradient-to-r from-blue-100 to-cyan-100" };
    if (score >= 400) return { level: "Money Smart", icon: Star, color: "from-green-400 to-emerald-500", bgColor: "bg-gradient-to-r from-green-100 to-emerald-100" };
    if (score >= 250) return { level: "Building Wealth", icon: TrendingUp, color: "from-indigo-400 to-blue-500", bgColor: "bg-gradient-to-r from-indigo-100 to-blue-100" };
    return { level: "Starting Journey", icon: Target, color: "from-gray-400 to-gray-500", bgColor: "bg-gradient-to-r from-gray-100 to-gray-200" };
  };

  const score = financeScoreData?.score || 0;
  const level = getFinanceLevel(score);
  const LevelIcon = level.icon;
  const progressToNext = ((score % 150) / 150) * 100; // Progress to next financial tier

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-white via-green-50/30 to-emerald-50/50 border-0 shadow-xl hover:shadow-2xl transition-all duration-500 group">
      {/* Animated financial background */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 via-emerald-600/5 to-teal-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      {/* Premium glassmorphism overlay */}
      <div className="absolute inset-0 bg-background/40 backdrop-blur-sm" />
      
      <CardContent className="relative p-8 space-y-6">
        {/* Header with Finance Score and level */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-secondary" />
              Finance Centse Score
            </h3>
            <p className="text-sm text-gray-600">Personal finance mastery rating</p>
          </div>
          
          <Badge className={`${level.bgColor} text-gray-700 border-0 px-3 py-1 font-semibold shadow-sm`}>
            <LevelIcon className="h-3 w-3 mr-1" />
            {level.level}
          </Badge>
        </div>

        {/* Main score display with enhanced animation */}
        <div className="text-center space-y-4">
          <div className="relative">
            <div className={`text-6xl font-black bg-gradient-to-r ${level.color} bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300`}>
              {animatedScore}
            </div>
            <div className="text-sm text-gray-500 font-medium">Financial Literacy Score</div>
          </div>

          {/* Progress to next financial level */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Progress to {financeScoreData?.insights?.nextMilestone || "next level"}</span>
              <span>{Math.round(progressToNext)}%</span>
            </div>
            <Progress 
              value={progressToNext} 
              className="h-2 bg-gray-200"
            />
          </div>
        </div>

        {/* Financial metrics breakdown */}
        <div className="grid grid-cols-4 gap-3 pt-4 border-t border-gray-200/50">
          <div className="text-center space-y-1 group/stat hover:scale-105 transition-transform duration-200">
            <div className="p-2 bg-blue-100 rounded-lg w-fit mx-auto group-hover/stat:bg-blue-200 transition-colors">
              <Shield className="h-4 w-4 text-blue-600" />
            </div>
            <div className="text-sm font-semibold text-gray-800">
              {financeScoreData?.metrics?.emergencyFundProgress || 0}%
            </div>
            <div className="text-xs text-gray-500">Emergency Fund</div>
          </div>

          <div className="text-center space-y-1 group/stat hover:scale-105 transition-transform duration-200">
            <div className="p-2 bg-green-100 rounded-lg w-fit mx-auto group-hover/stat:bg-green-200 transition-colors">
              <Target className="h-4 w-4 text-green-600" />
            </div>
            <div className="text-sm font-semibold text-gray-800">
              {financeScoreData?.metrics?.budgetCompliance || 0}%
            </div>
            <div className="text-xs text-gray-500">Budget Discipline</div>
          </div>

          <div className="text-center space-y-1 group/stat hover:scale-105 transition-transform duration-200">
            <div className="p-2 bg-purple-100 rounded-lg w-fit mx-auto group-hover/stat:bg-purple-200 transition-colors">
              <PiggyBank className="h-4 w-4 text-purple-600" />
            </div>
            <div className="text-sm font-semibold text-gray-800">
              {financeScoreData?.metrics?.savingsRate || 0}%
            </div>
            <div className="text-xs text-gray-500">Savings Rate</div>
          </div>

          <div className="text-center space-y-1 group/stat hover:scale-105 transition-transform duration-200">
            <div className="p-2 bg-orange-100 rounded-lg w-fit mx-auto group-hover/stat:bg-orange-200 transition-colors">
              <Users className="h-4 w-4 text-orange-600" />
            </div>
            <div className="text-sm font-semibold text-gray-800">
              {financeScoreData?.metrics?.friendsCount || 0}
            </div>
            <div className="text-xs text-gray-500">Finance Friends</div>
          </div>
        </div>

        {/* Score breakdown bars */}
        {financeScoreData?.breakdown && (
          <div className="space-y-3 pt-4 border-t border-gray-200/50">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Score Breakdown</h4>
            
            {Object.entries(financeScoreData.breakdown).map(([key, value]) => (
              <div key={key} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                  <span className="font-medium">{value}/100</span>
                </div>
                <Progress 
                  value={value} 
                  className="h-1.5"
                />
              </div>
            ))}
          </div>
        )}

        {/* Actionable finance insights */}
        <div className="text-center pt-2">
          {score >= 700 && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 font-medium">
                🎯 Excellent financial health! You're mastering money management.
              </p>
              <Button variant="outline" size="sm" className="bg-secondary/5 border-secondary/20 hover:bg-secondary/10">
                <BookOpen className="h-3 w-3 mr-1" />
                Advanced Investment Strategies
              </Button>
            </div>
          )}
          {score >= 400 && score < 700 && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 font-medium">
                💪 Strong foundation! Focus on {financeScoreData?.insights?.improvementArea || "emergency fund"} to level up.
              </p>
              <Button variant="outline" size="sm" className="bg-secondary/5 border-secondary/20 hover:bg-secondary/10">
                <BookOpen className="h-3 w-3 mr-1" />
                Financial Planning Basics
              </Button>
            </div>
          )}
          {score < 400 && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 font-medium">
                🚀 Building your financial future! Start with budgeting and emergency fund basics.
              </p>
              <Button variant="outline" size="sm" className="bg-secondary/5 border-secondary/20 hover:bg-secondary/10">
                <BookOpen className="h-3 w-3 mr-1" />
                Money Management 101
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CentseScoreFinance;